import React, { useEffect, useState } from 'react';
import { IconButton } from '@mui/material';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from '@shared/components/icons/svg-icon.tsx';
import DeleteDialog from '@shared/components/delete-dialog/delete-dialog.tsx';
import UnsavedChangesDialog from '@components/unsaved-changes-dialog/unsaved-changes-dialog.tsx';
import useGroupSettingsStore from './store/group-settings.store.ts';
import GroupSettingsHeader from './components/group-settings-header/group-settings-header.tsx';
import DefaultScreenOrPlaylist from './components/default-screen-or-playlist/default-screen-or-playlist.tsx';
import PlaylistSchedule from './components/playlist-schedule/playlist-schedule.tsx';
import GroupDevices from './components/group-devices/group-devices.tsx';
import GroupScreenPreview from './components/group-screen-preview/group-screen-preview.tsx';
import MoveDevicesDialog from './components/move-devices-dialog/move-devices-dialog.tsx';
import GroupSettingsFooter from '@modules/digital-signage/components/group-settings/components/group-settings-footer/group-settings-footer.tsx';
import { useGroupSettingsForm } from '@modules/digital-signage/components/group-settings/hooks/use-group-settings-form.ts';
import { usePreviewScreen } from '@modules/digital-signage/components/group-settings/hooks/use-preview-screen.ts';
import { useGroupSettingsClose } from '@modules/digital-signage/components/group-settings/hooks/use-group-settings-close.ts';
import {
  GroupSettingsFormValues,
  GroupSettingsProps
} from '@modules/digital-signage/model/group-settings.model.ts';
import { DevicesRowDataProps } from '@modules/digital-signage/model/devices.model.ts';
import {
  buildGroupSettingsReq,
  getDevicesFromOtherGroups,
  isRealDeviceGroup
} from '@modules/digital-signage/utils/digital-signage.utils.ts';
import { getGroupDeviceIds } from '@modules/digital-signage/components/group-settings/utils/group-settings.utils.ts';

const GroupSettings: React.FC<GroupSettingsProps> = ({
  group,
  deviceId,
  deviceName,
  tableState,
  onClose,
  onSaved
}) => {
  const {
    getScreensOptions,
    getPlaylistsOptions,
    getGroupDetails,
    getDevicesOptions,
    getDeviceGroupsList,
    groupDetails,
    devicesList,
    deviceGroupsList,
    editGroupSettings,
    reloadGroupDevices,
    deleteGroup,
    resetGroupDevices,
    isLoading
  } = useGroupSettingsStore();
  const { t } = useTranslation();
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [showMoveDialog, setShowMoveDialog] = useState<boolean>(false);
  const [moveSourceDevices, setMoveSourceDevices] = useState<
    DevicesRowDataProps[]
  >([]);

  const groupId = group?._id;
  const savedGroup = groupDetails?._id === groupId ? groupDetails : group;
  const isRealGroup = !!groupId && isRealDeviceGroup(savedGroup);
  const hasOpenDialog = showDeleteDialog || showMoveDialog;

  const {
    methods,
    watchedScreen,
    watchedPlaylist,
    isDirty,
    isDefaultGroup,
    isCreatingFromDefault,
    addedDevicesCount
  } = useGroupSettingsForm({ group, deviceId, deviceName });
  const { handleSubmit } = methods;
  const previewScreen = usePreviewScreen(watchedScreen, watchedPlaylist);
  const {
    blocker,
    hasChanges,
    showUnsavedDialog,
    requestClose,
    closeUnsavedDialog,
    handleDiscard
  } = useGroupSettingsClose({
    isDirty,
    hasOpenDialog,
    onClose
  });
  const isSaving = isLoading() && !hasOpenDialog && !showUnsavedDialog;

  const handleReloadGroup = () =>
    reloadGroupDevices(getGroupDeviceIds(savedGroup));

  const saveGroup = (data: GroupSettingsFormValues) => {
    const isCreate = !group || isCreatingFromDefault;

    editGroupSettings({
      id: isCreate ? undefined : groupId,
      body: buildGroupSettingsReq(
        data,
        useGroupSettingsStore.getState().schedules,
        isCreate,
        savedGroup
      ),
      state: tableState,
      handleClose: onClose,
      onSuccess: () => {
        onSaved?.();
        blocker.proceed?.();
      }
    });
  };

  const onSubmit = (data: GroupSettingsFormValues) => {
    const devicesFromOtherGroups = getDevicesFromOtherGroups(
      data.devices,
      devicesList,
      deviceGroupsList,
      groupId
    );

    if (!devicesFromOtherGroups.length) {
      saveGroup(data);
      return;
    }
    closeUnsavedDialog();
    setMoveSourceDevices(devicesFromOtherGroups);
    setShowMoveDialog(true);
  };

  useEffect(() => {
    getScreensOptions();
    getPlaylistsOptions();
    getDevicesOptions();
    getDeviceGroupsList();
    if (groupId) getGroupDetails(groupId);
    return resetGroupDevices;
  }, []);

  return (
    <div className="w-full h-full fixed top-0 left-0 bg-(--white) z-1200 flex flex-col gap-5 p-6 overflow-hidden">
      <IconButton
        className="absolute! top-6 right-6 z-10 w-10! h-10! border! border-solid! border-(--gray-8)! bg-(--gray-11)! hover:bg-(--gray-10)!"
        onClick={requestClose}
      >
        <CloseIcon className="w-4! h-4!" stroke="var(--gray-2)" />
      </IconButton>

      <GroupSettingsHeader group={group} deviceName={deviceName} />

      <FormProvider {...methods}>
        <form
          className="flex flex-col gap-4 flex-1 min-h-0"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="flex gap-3 flex-1 min-h-0">
            <div className="flex flex-col gap-3 min-h-0 shrink-0 min-w-90 w-90 overflow-y-auto overflow-x-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <DefaultScreenOrPlaylist />
              <PlaylistSchedule groupId={groupId} />
            </div>

            <GroupScreenPreview screen={previewScreen} />

            <div className="flex flex-col gap-3 min-h-0 shrink-0 min-w-90 w-90 pb-16">
              <GroupDevices
                isDefaultGroup={isDefaultGroup}
                addedDevicesCount={addedDevicesCount}
                {...(isRealGroup && {
                  onReloadGroup: handleReloadGroup,
                  onDeleteGroup: () => setShowDeleteDialog(true)
                })}
              />
            </div>
          </div>
          <GroupSettingsFooter
            hasChanges={hasChanges}
            isCreatingFromDefault={isCreatingFromDefault}
            isLoading={isSaving}
          />
        </form>

        <MoveDevicesDialog
          open={showMoveDialog}
          sourceDevices={moveSourceDevices}
          isLoading={isLoading()}
          onClose={() => setShowMoveDialog(false)}
          onSave={handleSubmit(saveGroup)}
        />

        <UnsavedChangesDialog
          open={showUnsavedDialog}
          isLoading={isLoading()}
          onCancel={closeUnsavedDialog}
          onDiscard={handleDiscard}
          onSave={handleSubmit(onSubmit, closeUnsavedDialog)}
        />
      </FormProvider>

      <DeleteDialog
        open={showDeleteDialog}
        title={t('delete_popup_title')}
        isLoading={isLoading()}
        onCancel={() => setShowDeleteDialog(false)}
        onDelete={() =>
          groupId &&
          deleteGroup({
            id: groupId,
            state: tableState,
            handleClose: onClose,
            onSuccess: onSaved
          })
        }
      />
    </div>
  );
};

export default GroupSettings;
